<template>
    <div class="modal is-active" v-if="show">
        <div class="modal-background" @click="cancel"></div>
        <div class="modal-card kb-publish-modal">
            <header class="modal-card-head">
                <p class="modal-card-title">{{ $t('knowledgebase.readyToPublish') }}</p>
                <button class="delete" @click.prevent="cancel"></button>
            </header>
            <section class="modal-card-body">
                <p class="kb-publish-modal__empty" v-if="entries.length === 0">
                    {{ $t('knowledgebase.nothingToPublish') }}
                </p>
                <ul class="kb-publish-modal__list" v-else>
                    <li
                        v-for="entry in entries"
                        :key="entry.key"
                        class="kb-publish-modal__row"
                        :class="[
                            `kb-publish-modal__row--depth-${entry.depth || 0}`,
                            entry.kind === 'cascade' ? 'kb-publish-modal__row--cascade' : '',
                        ]"
                    >
                        <kb-dot v-if="entry.kind !== 'cascade'" class="kb-publish-modal__dot" />
                        <span class="kb-publish-modal__cascade-arrow" v-else>↳</span>
                        <div class="kb-publish-modal__row-body">
                            <div class="kb-publish-modal__row-title">{{ entry.title }}</div>
                            <div class="kb-publish-modal__row-context" v-if="entry.context">
                                {{ entry.context }}
                            </div>
                            <kb-line-diff
                                v-if="entry.newText != null"
                                :old-text="entry.oldText || ''"
                                :new-text="entry.newText || ''"
                            />
                        </div>
                    </li>
                </ul>
                <p class="kb-publish-modal__hint">
                    {{ $t('knowledgebase.publishHint') }}
                </p>
            </section>
            <footer class="modal-card-foot is-clearfix">
                <button
                    class="button is-primary"
                    :class="{ 'is-loading': busy }"
                    :disabled="entries.length === 0 || busy"
                    @click="publish"
                >
                    {{ $t('knowledgebase.publishAll') }}
                </button>
                <button
                    class="button is-danger is-outlined"
                    :disabled="entries.length === 0 || busy"
                    @click="discard"
                >
                    {{ $t('knowledgebase.discardAll') }}
                </button>
                <button class="button" @click="cancel">{{ $t('generic.cancel') }}</button>
            </footer>
        </div>
    </div>
</template>

<script>
import KbDot from './kb-dot.vue';
import KbLineDiff from './kb-line-diff.vue';

export default {
    name: 'kb-publish-modal',

    components: {KbDot, KbLineDiff},

    data() {
        return {
            show: false,
            busy: false,
            topics: [],
            articles: [],
            seasons: [],
            connections: [],
            resolver: null,
        };
    },

    computed: {
        seasonsById() {
            const map = {};
            for(const s of this.seasons) map[s.id] = s;
            return map;
        },
        connectionsById() {
            const map = {};
            for(const c of this.connections) map[c.id] = c;
            return map;
        },
        entries() {
            const out = [];
            const topicsById = {};
            for(const t of this.topics) topicsById[t.id] = t;
            const seasonsById = this.seasonsById;

            const articlesByTopic = {};
            for(const a of this.articles) {
                if(!articlesByTopic[a.topic]) articlesByTopic[a.topic] = [];
                articlesByTopic[a.topic].push(a);
            }

            const untitledTopic = () => this.$t('knowledgebase.untitledTopic');
            const untitledArticle = () => this.$t('knowledgebase.untitledArticle');

            // Emit a cascade-delete entry for every descendant of a staged row,
            // so the user can see exactly what will go away when they publish.
            const emitArticleCascade = (article, depth) => {
                out.push({
                    key: `cascade-article-${article.id}`,
                    kind: 'cascade',
                    depth,
                    title: this.$t('knowledgebase.entryCascadeArticle', {
                        article: article.title || untitledArticle(),
                    }),
                    context: '',
                });
                for(const v of (article.variants || [])) {
                    out.push({
                        key: `cascade-variant-${v.id}`,
                        kind: 'cascade',
                        depth: depth + 1,
                        title: this.$t('knowledgebase.entryCascadeVariant', {
                            article: article.title || untitledArticle(),
                        }),
                        context: '',
                    });
                }
            };

            for(const t of this.topics) {
                if(t.has_draft && !t.will_be_deleted) {
                    out.push({
                        key: `topic-${t.id}-title`,
                        kind: 'topic_title',
                        depth: 0,
                        title: this.$t('knowledgebase.entryTopicRename', {
                            now: t.title_draft || untitledTopic(),
                        }),
                        context: t.title_published
                            ? this.$t('knowledgebase.entryWas', {was: t.title_published})
                            : this.$t('knowledgebase.entryNeverPublished'),
                        oldText: t.title_published || '',
                        newText: t.title_draft || '',
                    });
                }
                if(t.is_pending_deletion) {
                    out.push({
                        key: `topic-${t.id}-delete`,
                        kind: 'topic_delete',
                        depth: 0,
                        title: this.$t('knowledgebase.entryTopicDelete', {
                            topic: t.title || untitledTopic(),
                        }),
                        context: '',
                    });
                    for(const a of (articlesByTopic[t.id] || [])) {
                        emitArticleCascade(a, 1);
                    }
                }
            }

            for(const a of this.articles) {
                if(a.will_be_deleted && !a.is_pending_deletion) {
                    // Already covered by its topic's cascade above.
                    continue;
                }
                const topic = topicsById[a.topic];
                const inTopic = topic
                    ? this.$t('knowledgebase.entryInTopic', {topic: topic.title || ''})
                    : '';

                if(a.title_draft != null && !a.will_be_deleted) {
                    out.push({
                        key: `article-${a.id}-title`,
                        kind: 'article_title',
                        depth: 0,
                        title: a.title_published
                            ? this.$t('knowledgebase.entryArticleRename', {now: a.title_draft})
                            : this.$t('knowledgebase.entryArticleNew', {now: a.title_draft}),
                        context: inTopic,
                        oldText: a.title_published || '',
                        newText: a.title_draft || '',
                    });
                }
                if(a.is_pending_deletion) {
                    out.push({
                        key: `article-${a.id}-delete`,
                        kind: 'article_delete',
                        depth: 0,
                        title: this.$t('knowledgebase.entryArticleDelete', {
                            article: a.title || untitledArticle(),
                        }),
                        context: inTopic,
                    });
                    for(const v of (a.variants || [])) {
                        out.push({
                            key: `cascade-variant-${v.id}`,
                            kind: 'cascade',
                            depth: 1,
                            title: this.$t('knowledgebase.entryCascadeVariant', {
                                article: a.title || untitledArticle(),
                            }),
                            context: '',
                        });
                    }
                    continue;
                }
                // Visibility changes on this article — listed alongside its
                // content edits so the user sees everything that will apply
                // when they publish, in one place. Skipped for articles staged
                // for deletion because they're going away anyway.
                if(!a.will_be_deleted) {
                    const connectionsById = this.connectionsById;
                    for(const ex of (a.channel_exclusions || [])) {
                        const conn = connectionsById[ex.connection];
                        const channelName = conn ? conn.channel_name : `#${ex.connection}`;
                        const articleLabel = a.title || untitledArticle();
                        if(ex.state === 'staged_add') {
                            out.push({
                                key: `exclusion-${ex.id}-add`,
                                kind: 'visibility',
                                depth: 0,
                                title: this.$t('knowledgebase.entryArticleHide', {
                                    article: articleLabel,
                                    channel: channelName,
                                }),
                                context: inTopic,
                            });
                        } else if(ex.state === 'staged_remove') {
                            out.push({
                                key: `exclusion-${ex.id}-remove`,
                                kind: 'visibility',
                                depth: 0,
                                title: this.$t('knowledgebase.entryArticleShow', {
                                    article: articleLabel,
                                    channel: channelName,
                                }),
                                context: inTopic,
                            });
                        }
                    }
                }

                for(const v of (a.variants || [])) {
                    const articleLabel = a.title || untitledArticle();
                    const season = v.season != null ? seasonsById[v.season] : null;
                    const sName = season ? season.name : '';
                    const isAnnouncement = v.valid_from != null;
                    const annLabel = v.label || this.$t('knowledgebase.announcementUntitled');
                    if(v.body_draft != null && !v.will_be_deleted) {
                        let title;
                        if(isAnnouncement) {
                            title = this.$t('knowledgebase.entryVariantBodyAnnouncement', {
                                article: articleLabel,
                                announcement: annLabel,
                            });
                        } else if(sName) {
                            title = this.$t('knowledgebase.entryVariantBodySeasonal', {
                                article: articleLabel,
                                season: sName,
                            });
                        } else {
                            title = this.$t('knowledgebase.entryVariantBody', {
                                article: articleLabel,
                            });
                        }
                        out.push({
                            key: `variant-${v.id}-body`,
                            kind: 'variant_body',
                            depth: 0,
                            title,
                            context: inTopic,
                            oldText: v.body_published || '',
                            newText: v.body_draft || '',
                        });
                    }
                    if(v.is_pending_deletion) {
                        let title;
                        if(isAnnouncement) {
                            title = this.$t('knowledgebase.entryVariantDeleteAnnouncement', {
                                article: articleLabel,
                                announcement: annLabel,
                            });
                        } else if(sName) {
                            title = this.$t('knowledgebase.entryVariantDeleteSeasonal', {
                                article: articleLabel,
                                season: sName,
                            });
                        } else {
                            title = this.$t('knowledgebase.entryVariantDelete', {
                                article: articleLabel,
                            });
                        }
                        out.push({
                            key: `variant-${v.id}-delete`,
                            kind: 'variant_delete',
                            depth: 0,
                            title,
                            context: inTopic,
                        });
                    }
                }
            }
            return out;
        },
    },

    methods: {
        open({topics, articles, seasons, connections}) {
            this.topics = topics;
            this.articles = articles;
            this.seasons = seasons || [];
            this.connections = connections || [];
            this.show = true;
            return new Promise(resolve => {
                this.resolver = resolve;
            });
        },
        async publish() {
            this.busy = true;
            try {
                this.$emit('publish');
                this._resolve('published');
            } finally {
                this.busy = false;
                this.show = false;
            }
        },
        async discard() {
            this.busy = true;
            try {
                this.$emit('discard');
                this._resolve('discarded');
            } finally {
                this.busy = false;
                this.show = false;
            }
        },
        cancel() {
            this._resolve(null);
            this.show = false;
        },
        _resolve(result) {
            const r = this.resolver;
            this.resolver = null;
            if(r) r(result);
        },
    },
};
</script>

<style lang="scss" scoped>
.kb-publish-modal {
    max-width: 560px;
    margin: 0 auto;

    &__empty {
        color: #888;
        margin: 16px 0;
    }

    &__list {
        max-height: 320px;
        overflow-y: auto;
        margin: 0 0 12px;
        padding: 0;
        list-style: none;
        border: 1px solid #eee;
        border-radius: 4px;
    }

    &__row {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 10px 12px;
        border-bottom: 1px solid #f1f1f1;

        &:last-child {
            border-bottom: 0;
        }

        &--depth-1 {
            padding-left: 32px;
        }

        &--depth-2 {
            padding-left: 52px;
        }

        &--cascade {
            color: #999;
            font-size: 0.9rem;
            background: #fafafa;
        }
    }

    &__cascade-arrow {
        color: #bbb;
        margin-top: 1px;
        flex-shrink: 0;
    }

    &__row-body {
        flex: 1;
        min-width: 0;
    }

    &__row-title {
        font-size: 0.95rem;
        font-weight: 500;
    }

    &__row-context {
        font-size: 0.8rem;
        color: #888;
        margin-top: 2px;
    }

    &__hint {
        font-size: 0.8rem;
        color: #999;
        margin-top: 4px;
    }

    &__dot {
        flex-shrink: 0;
        margin-top: 6px;
    }
}
</style>



// WEBPACK FOOTER //
// src/views/pages/knowledge-base/kb-publish-modal.vue